import { useAtomValue } from "jotai";
import Cookies from "js-cookie";
import {
  type FunctionComponent,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { accountAtom } from "shared/lib/account";

import { appAtom } from "scmp-app/lib/app/atoms";
import type { batchQueryAbWidgetQuery } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";

import { fetchAbWidgetRecommendations, getColdStart, massageApiResponse } from "./api";
import { AbWidgetBatchQuery } from "./batch-query";
import { AB_WIDGET_FETCH_LOG_PREFIX, AbWidgetFetchVariant } from "./constants";
import { AbWidgetControlLoader } from "./control-query-loader";
import { TreatmentErrorBoundary } from "./error-boundary";
import { useAbWidgetVariant } from "./hooks";
import type { AbWidgetEntityIds } from "./types";

export type ControlQueryVariables = {
  chinaFutureTechSectionEntityId: string;
  chinaFutureTechStoryPackageArticlesQueueName: string;
  secondaryStoryPackageArticlesQueueName: string;
  seriesWidgetArticlesQueueName: string;
  spotlightQueueName: string;
};

export type FallbackReason =
  | "api_error"
  | "api_timeout"
  | "empty_entity_ids"
  | "render_error"
  | "vi_timeout";

export type WidgetVersion = "cold_start" | "qualified_reader";

type BatchData = batchQueryAbWidgetQuery["response"];

type Props = {
  controlQueryVariables: ControlQueryVariables;
  onReady: (entityIds: string[]) => void;
  renderControlWidgets: (
    queryReference: unknown,
    fallbackReason?: FallbackReason,
    widgetVersion?: WidgetVersion,
  ) => ReactNode;
  renderPlaceholders: () => ReactNode;
  renderTreatmentWidgets: (
    batchData: BatchData,
    topicNames: {
      cftTopicName?: string;
      openQuestionTopicName?: string;
      seriesTopicName?: string;
    },
    widgetVersion?: WidgetVersion,
    recommendationId?: string,
  ) => ReactNode;
  ssrContentEntityIds: string[];
};

const shouldUseControlPath = (variant: AbWidgetFetchVariant | null, coldStart: boolean): boolean =>
  variant === AbWidgetFetchVariant.Control || (variant === AbWidgetFetchVariant.V1 && coldStart);

const hasEmptyEntityIds = (result: AbWidgetEntityIds): boolean =>
  result.series.length === 0 ||
  result.openQuestion.length === 0 ||
  result.cft.length === 0 ||
  result.spotlight.length === 0;

const collectAllEntityIds = (result: AbWidgetEntityIds): string[] => [
  ...result.series,
  ...result.openQuestion,
  ...result.cft,
  ...result.spotlight,
];

export const AbWidgetFetch: FunctionComponent<Props> = ({
  controlQueryVariables,
  onReady,
  renderControlWidgets,
  renderPlaceholders,
  renderTreatmentWidgets,
  ssrContentEntityIds,
}) => {
  const { isLoading: variantLoading, variant } = useAbWidgetVariant();
  const [apiResult, setApiResult] = useState<AbWidgetEntityIds | null>(null);
  const [coldStart, setColdStart] = useState(false);
  const [fallbackToControl, setFallbackToControl] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<FallbackReason | undefined>(undefined);
  const fetchStartedReference = useRef(false);
  const onReadyFiredReference = useRef(false);

  const { navigatorInfo } = useAtomValue(appAtom);
  const { user } = useAtomValue(accountAtom);

  const fireOnReady = useCallback(
    (entityIds: string[]) => {
      if (onReadyFiredReference.current) return;
      onReadyFiredReference.current = true;
      onReady(entityIds);
    },
    [onReady],
  );

  const handleTreatmentError = useCallback(() => {
    console.error(
      `${AB_WIDGET_FETCH_LOG_PREFIX} Treatment rendering error — falling back to control widgets.`,
    );
    setFallbackReason("render_error");
    setFallbackToControl(true);
  }, []);

  const isControlPath = fallbackToControl || shouldUseControlPath(variant, coldStart) || !variant;
  const widgetVersion: undefined | WidgetVersion = fallbackToControl
    ? undefined
    : isControlPath
      ? variant === AbWidgetFetchVariant.V1 && coldStart
        ? "cold_start"
        : undefined
      : coldStart
        ? "cold_start"
        : "qualified_reader";

  const renderControlPath = useCallback(
    () => (
      <AbWidgetControlLoader
        onEntityIds={fireOnReady}
        renderPlaceholders={renderPlaceholders}
        variables={controlQueryVariables}
      >
        {data => renderControlWidgets(data, fallbackReason, widgetVersion)}
      </AbWidgetControlLoader>
    ),
    [
      controlQueryVariables,
      fallbackReason,
      fireOnReady,
      renderControlWidgets,
      renderPlaceholders,
      widgetVersion,
    ],
  );

  // Fetch API for v1/v2 when variant is determined — only once
  useEffect(() => {
    if (!variant) {
      fireOnReady([]);
      return;
    }
    if (variant === AbWidgetFetchVariant.Control) {
      return;
    }
    if (fetchStartedReference.current) return;

    fetchStartedReference.current = true;

    const VI_POLL_INTERVAL_MS = 1000;
    const VI_POLL_TIMEOUT_MS = 5000;

    const fetchApi = async (visitorId: string) => {
      try {
        const response = await fetchAbWidgetRecommendations({
          excludeArticleIds: ssrContentEntityIds,
          reqRegion: navigatorInfo?.country ?? "global",
          userId: user?.uuid,
          visitorId,
        });
        const entityIds = massageApiResponse(response);
        const apiColdStart = getColdStart(response);
        setColdStart(apiColdStart);
        setApiResult(entityIds);
      } catch (error) {
        const isTimeout = error instanceof DOMException && error.name === "AbortError";
        const reason = isTimeout ? "api_timeout" : "api_error";
        console.error(
          `${AB_WIDGET_FETCH_LOG_PREFIX} API fetch failed (${reason}) — falling back to control widgets.`,
          error,
        );
        setFallbackReason(reason);
        setFallbackToControl(true);
      }
    };

    const viBeforeFetch = Cookies.get("_vi") ?? "";

    if (viBeforeFetch) {
      void fetchApi(viBeforeFetch);
    } else {
      let elapsed = 0;
      const poll = setInterval(() => {
        elapsed += VI_POLL_INTERVAL_MS;
        const viNow = Cookies.get("_vi");
        if (viNow) {
          clearInterval(poll);
          void fetchApi(viNow);
        } else if (elapsed >= VI_POLL_TIMEOUT_MS) {
          clearInterval(poll);
          setFallbackReason("vi_timeout");
          setFallbackToControl(true);
        }
      }, VI_POLL_INTERVAL_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  // Fire onReady for treatment path after API result is available and we know
  // we're rendering treatment (not falling back to control)
  useEffect(() => {
    if (!apiResult) return;
    if (fallbackToControl) return;
    if (shouldUseControlPath(variant, coldStart)) return;
    if (hasEmptyEntityIds(apiResult)) return;
    fireOnReady(collectAllEntityIds(apiResult));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResult, fallbackToControl, coldStart]);

  // Phase 1: SSR or client before Optimizely loads → placeholder
  if (variantLoading) {
    return <>{renderPlaceholders()}</>;
  }

  // Optimizely initialized but no experiment matched — render control UI
  if (!variant) {
    return <>{renderControlPath()}</>;
  }

  // Any error or fallback → control widgets
  if (fallbackToControl) {
    return <>{renderControlPath()}</>;
  }

  // Phase 2: Determine rendering path based on variant + coldStart
  if (shouldUseControlPath(variant, coldStart)) {
    return <>{renderControlPath()}</>;
  }

  if (!apiResult) {
    return <>{renderPlaceholders()}</>;
  }

  // Safety: if any widget has empty entityIds, batch query will fail with negative limit
  if (hasEmptyEntityIds(apiResult)) {
    console.warn(
      `${AB_WIDGET_FETCH_LOG_PREFIX} Some widgets have empty entityIds — falling back to control path.`,
      apiResult,
    );
    setFallbackReason("empty_entity_ids");
    return <>{renderControlPath()}</>;
  }

  // Treatment path — batch query for article details, render copied components
  return (
    <TreatmentErrorBoundary onError={handleTreatmentError}>
      <Suspense fallback={<>{renderPlaceholders()}</>}>
        <AbWidgetBatchQuery
          cftEntityIds={apiResult.cft}
          cftTopicEntityId={apiResult.cftTopicEntityId}
          openQuestionEntityIds={apiResult.openQuestion}
          openQuestionTopicEntityId={apiResult.openQuestionTopicEntityId}
          seriesEntityIds={apiResult.series}
          seriesTopicEntityId={apiResult.seriesTopicEntityId}
          spotlightEntityIds={apiResult.spotlight}
        >
          {data =>
            renderTreatmentWidgets(
              data,
              {
                cftTopicName: apiResult.cftTopicName,
                openQuestionTopicName: apiResult.openQuestionTopicName,
                seriesTopicName: apiResult.seriesTopicName,
              },
              widgetVersion,
              apiResult.recommendationId,
            )
          }
        </AbWidgetBatchQuery>
      </Suspense>
    </TreatmentErrorBoundary>
  );
};

AbWidgetFetch.displayName = "AbWidgetFetch";
